import ChecklistContainer from './ChecklistContainer';
import { MessageTypes } from '../types/MessageTypes';
import { clampChecklistLayout } from '../../checklist/helpers/checklistLayoutHelper';

/**
 * Resize/position flow:
 *
 *   iframe (Checklist.tsx) sends RESIZE_AND_POSITION_CHECKLIST_IFRAME with
 *   the *desired* size and offsets.
 *
 *   This class preserves the desired values in `lastPositionData` (unclamped)
 *   so that when the host viewport later grows back, we can restore the
 *   original intent. Before applying to the iframe, we run the desired values
 *   through `clampChecklistLayout` against `window.innerWidth/Height` so the
 *   iframe never exceeds the viewport.
 *
 *   On `window.resize`, we re-clamp `lastPositionData` against the new
 *   viewport and re-apply.
 */
class Checklist {
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private resizeListener: (() => void) | null = null;
  private outsideClickListener: ((event: PointerEvent) => void) | null = null;
  private lastPositionData: {
    position: string;
    dimensions: { width: number; height: number };
    offsets: { vertical: number; horizontal: number };
    mode: 'panel' | 'launcher';
    referenceDimensions?: { width: number; height: number };
  } | null = null;
  private dragOverlay: HTMLDivElement | null = null;
  private dragCleanup: (() => void) | null = null;

  constructor() {
    this.startListeners();
  }

  private calculateIframePosition(
    position: string,
    _dimensions: { width: number; height: number },
    offsets: { vertical: number; horizontal: number } = { vertical: 0, horizontal: 0 }
  ): {
    top: string;
    left: string;
    bottom: string;
    right: string;
    transform: string;
  } {
    const v = offsets.vertical;
    const h = offsets.horizontal;

    const auto = 'auto';
    const none = 'none';

    switch (position) {
      case 'top-left':
        return {
          top: `${v}px`,
          left: `${h}px`,
          bottom: auto,
          right: auto,
          transform: none
        };
      case 'top-right':
        return {
          top: `${v}px`,
          left: auto,
          bottom: auto,
          right: `${h}px`,
          transform: none
        };
      case 'bottom-left':
        return {
          top: auto,
          left: `${h}px`,
          bottom: `${v}px`,
          right: auto,
          transform: none
        };
      case 'bottom-right':
        return {
          top: auto,
          left: auto,
          bottom: `${v}px`,
          right: `${h}px`,
          transform: none
        };
      case 'top-center':
        return {
          top: `${v}px`,
          left: '50%',
          bottom: auto,
          right: auto,
          transform: 'translateX(-50%)'
        };
      case 'bottom-center':
        return {
          top: auto,
          left: '50%',
          bottom: `${v}px`,
          right: auto,
          transform: 'translateX(-50%)'
        };
      // Aba vertical colada na borda: `v` desloca a partir do centro da tela.
      case 'edge-right':
        return {
          top: `calc(50% + ${v}px)`,
          left: auto,
          bottom: auto,
          right: '0px',
          transform: 'translateY(-50%)'
        };
      case 'edge-left':
        return {
          top: `calc(50% + ${v}px)`,
          left: '0px',
          bottom: auto,
          right: auto,
          transform: 'translateY(-50%)'
        };
      default:
        return {
          top: auto,
          left: auto,
          bottom: `${v}px`,
          right: `${h}px`,
          transform: none
        };
    }
  }

  private sameLayout(
    a: {
      position: string;
      dimensions: { width: number; height: number };
      offsets: { vertical: number; horizontal: number };
      mode: 'panel' | 'launcher';
    },
    b: {
      position: string;
      dimensions: { width: number; height: number };
      offsets: { vertical: number; horizontal: number };
      mode: 'panel' | 'launcher';
    }
  ): boolean {
    return (
      a.position === b.position &&
      a.dimensions.width === b.dimensions.width &&
      a.dimensions.height === b.dimensions.height &&
      a.offsets.vertical === b.offsets.vertical &&
      a.offsets.horizontal === b.offsets.horizontal &&
      a.mode === b.mode
    );
  }

  private handleResize = () => {
    if (this.lastPositionData) {
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const clamped = clampChecklistLayout({
        position: this.lastPositionData.position,
        dimensions: this.lastPositionData.dimensions,
        offsets: this.lastPositionData.offsets,
        viewport,
        applyMinFloor: this.lastPositionData.mode === 'panel',
        referenceDimensions: this.lastPositionData.referenceDimensions
      });
      const calculatedPosition = this.calculateIframePosition(
        this.lastPositionData.position,
        clamped.dimensions,
        clamped.offsets
      );

      ChecklistContainer.getInstance().resizeAndPosition(calculatedPosition, {
        width: `${clamped.dimensions.width}px`,
        height: `${clamped.dimensions.height}px`
      });
    }
  };

  private startHostDrag(grabOffsetX: number, grabOffsetY: number, edge = false): void {
    const iframe = ChecklistContainer.getInstance().getContainer();
    if (!iframe) return;

    this.endHostDrag(false);

    const rect = iframe.getBoundingClientRect();
    iframe.style.setProperty('top', `${rect.top}px`);
    iframe.style.setProperty('left', `${rect.left}px`);
    iframe.style.setProperty('bottom', 'auto');
    iframe.style.setProperty('right', 'auto');
    iframe.style.setProperty('transform', 'none');
    iframe.style.setProperty('width', `${rect.width}px`);
    iframe.style.setProperty('height', `${rect.height}px`);
    this.lastPositionData = null;

    const overlay = document.createElement('div');
    overlay.toggleAttribute('getdemo-checklist-drag-overlay', true);
    overlay.style.setProperty('position', 'fixed');
    overlay.style.setProperty('top', '0');
    overlay.style.setProperty('left', '0');
    overlay.style.setProperty('width', '100vw');
    overlay.style.setProperty('height', '100vh');
    overlay.style.setProperty('z-index', '2147483647');
    overlay.style.setProperty('background', 'transparent');
    overlay.style.setProperty('cursor', 'grabbing');
    overlay.style.setProperty('touch-action', 'none');
    overlay.style.setProperty('user-select', 'none');
    document.documentElement.appendChild(overlay);
    this.dragOverlay = overlay;

    const onMove = (ev: PointerEvent) => {
      const w = iframe.offsetWidth;
      const h = iframe.offsetHeight;
      const maxL = Math.max(0, window.innerWidth - w);
      const maxT = Math.max(0, window.innerHeight - h);
      // A aba de borda só sobe e desce; passar do meio da tela a leva para a outra lateral.
      const nl = edge
        ? ev.clientX < window.innerWidth / 2
          ? 0
          : maxL
        : Math.max(0, Math.min(ev.clientX - grabOffsetX, maxL));
      const nt = Math.max(0, Math.min(ev.clientY - grabOffsetY, maxT));
      iframe.style.setProperty('top', `${nt}px`);
      iframe.style.setProperty('left', `${nl}px`);
    };

    const onUp = () => {
      this.endHostDrag(true);
    };

    overlay.addEventListener('pointermove', onMove);
    overlay.addEventListener('pointerup', onUp);
    overlay.addEventListener('pointercancel', onUp);

    this.dragCleanup = () => {
      overlay.removeEventListener('pointermove', onMove);
      overlay.removeEventListener('pointerup', onUp);
      overlay.removeEventListener('pointercancel', onUp);
    };
  }

  private endHostDrag(sendEndMessage: boolean): void {
    if (this.dragCleanup) {
      this.dragCleanup();
      this.dragCleanup = null;
    }
    if (this.dragOverlay) {
      if (this.dragOverlay.parentNode) {
        this.dragOverlay.parentNode.removeChild(this.dragOverlay);
      }
      this.dragOverlay = null;
    }
    if (!sendEndMessage) return;
    const iframe = ChecklistContainer.getInstance().getContainer();
    if (!iframe) return;
    const rect = iframe.getBoundingClientRect();
    ChecklistContainer.getInstance().sendMessage({
      type: MessageTypes.END_CHECKLIST_DRAG,
      iframeRect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });
  }

  private startListeners() {
    this.outsideClickListener = (event: PointerEvent) => {
      if (this.dragOverlay) return;

      const iframe = ChecklistContainer.getInstance().getContainer();
      if (!iframe) return;

      const rect = iframe.getBoundingClientRect();
      const clickedInsideIframe =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (clickedInsideIframe) return;

      ChecklistContainer.getInstance().sendMessage({
        type: MessageTypes.CLOSE_CHECKLIST
      });
    };

    this.messageListener = (event: MessageEvent) => {
      if (event.data?.type === MessageTypes.START_CHECKLIST_DRAG) {
        const grabOffsetX = Number(event.data.grabOffsetX) || 0;
        const grabOffsetY = Number(event.data.grabOffsetY) || 0;
        this.startHostDrag(grabOffsetX, grabOffsetY, event.data.edge === true);
        return;
      }
      if (event.data?.type !== MessageTypes.RESIZE_AND_POSITION_CHECKLIST_IFRAME) {
        return;
      }

      const { position, dimensions } = event.data;
      const verticalOffset = Number(event.data.verticalOffset) || 0;
      const horizontalOffset = Number(event.data.horizontalOffset) || 0;
      const offsets = { vertical: verticalOffset, horizontal: horizontalOffset };
      const mode: 'panel' | 'launcher' = event.data.mode === 'launcher' ? 'launcher' : 'panel';
      const referenceDimensions =
        event.data.referenceDimensions &&
        typeof event.data.referenceDimensions.width === 'number' &&
        typeof event.data.referenceDimensions.height === 'number'
          ? {
              width: event.data.referenceDimensions.width,
              height: event.data.referenceDimensions.height
            }
          : undefined;

      const incoming = { position, dimensions, offsets, mode, referenceDimensions };

      const duplicate =
        this.lastPositionData !== null && this.sameLayout(this.lastPositionData, incoming);

      if (!duplicate) {
        this.lastPositionData = incoming;

        const viewport = { width: window.innerWidth, height: window.innerHeight };
        const clamped = clampChecklistLayout({
          ...incoming,
          viewport,
          applyMinFloor: mode === 'panel'
        });
        const calculatedPosition = this.calculateIframePosition(
          position,
          clamped.dimensions,
          clamped.offsets
        );

        ChecklistContainer.getInstance().resizeAndPosition(calculatedPosition, {
          width: `${clamped.dimensions.width}px`,
          height: `${clamped.dimensions.height}px`
        });

        ChecklistContainer.getInstance().show();
      }
    };

    this.resizeListener = this.handleResize;

    window.addEventListener('message', this.messageListener);
    window.addEventListener('resize', this.resizeListener);
    document.addEventListener('pointerdown', this.outsideClickListener, true);
  }

  public destroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }

    if (this.outsideClickListener) {
      document.removeEventListener('pointerdown', this.outsideClickListener, true);
      this.outsideClickListener = null;
    }

    this.endHostDrag(false);
    this.lastPositionData = null;
  }
}

export default Checklist;
